/**
 * Store de pré-calcul centralisé pour les tiles du plan de monte.
 *
 * Construit des index Maps réactifs pour éviter les O(N*M) par tile,
 * et pré-calcule les données de chaque verrat en une seule passe.
 */
import { defineStore } from "pinia";
import { computed } from "vue";

import { useProductionStore } from "../production.store";
import { useLotStore } from "../lot.store";
import { useAttachmentStore } from "../attachment.store";
import { useGroupMountingStore } from "../groupMounting.store";
import { useCubicleStore } from "../cubicle.store";
import { useProductionAnalyseStore } from "../productionAnalyse.store";
import { useOptions } from "../options.store";
import { useMountingPlanStore } from "../mountingPlan.store";
import { useDispatchManufactoringStore } from "../dispatchManufactoring.store";
import { useSanitaryProtocolStore } from "../sanitaire/sanitaryProtocol.store";
import { useBoars } from "../boars.store";
import { useSectorCubicleStore } from "../sectorCubicle.store";

import { IProduction, StateOfProduction } from "../../models/production";
import { ILot } from "../../models/lot";
import { IAttachment } from "../../models/attachment";
import { IProductionAnalyse } from "../../models/productionAnalyse";
import { IBoar } from "../../models/boar";
import { ICubicle } from "../../models/cubicle";
import { ISector } from "../../models/sector";

import { numberJourBetween, getDateDiff } from "../../helper/dateFormatter";
import { CONCENTRATION_FINALE_OPTION, VOLUME_STANDARD_OPTION } from "../../constantes/options-constantes";
import { MountingPlanPremiumBoarOptions as PREMIUM_OPTIONS } from "../../constantes/constants";

export interface TileBoarData {
    schemaCode: string
    name: string
    rawName: string
    workNumber: string
    armada: boolean
    nnCode: string
    studInfo: string
    spzAverage: string
    group: string
    groupColor: string
    conservationStatus: string
    conservationApt: string
    lastProdInterval: string
    premium: boolean
    closePlanIds: number[]
}

export const useMountingPlanTileDataStore = defineStore("mountingPlanTileData", () => {

    // === Stores ===
    const productionStore = useProductionStore();
    const lotStore = useLotStore();
    const attachmentStore = useAttachmentStore();
    const groupMountingStore = useGroupMountingStore();
    const cubicleStore = useCubicleStore();
    const productionAnalysisStore = useProductionAnalyseStore();
    const optionStore = useOptions();
    const mountingPlanStore = useMountingPlanStore();
    const dispatchManufactoringStore = useDispatchManufactoringStore();
    const sanitaryProtocolStore = useSanitaryProtocolStore();
    const boarsStore = useBoars();
    const sectorCubicleStore = useSectorCubicleStore();

    // ==========================================
    // INDEX MAPS - recalculées quand les données source changent
    // ==========================================

    /** Productions indexées par animal ID */
    const productionsByAnimalId = computed(() => {
        const map = new Map<number, IProduction[]>();
        for (const p of productionStore.datas) {
            const animalId = p.animal?.id;
            if (!animalId) continue;
            let list = map.get(animalId);
            if (!list) { list = []; map.set(animalId, list); }
            list.push(p);
        }
        return map;
    });

    /** Lots indexés par production ID */
    const lotsByProductionId = computed(() => {
        const map = new Map<number, ILot[]>();
        for (const l of lotStore.datas) {
            const prodId = l.production?.id;
            if (!prodId) continue;
            let list = map.get(prodId);
            if (!list) { list = []; map.set(prodId, list); }
            list.push(l);
        }
        return map;
    });

    /** Attachments indexés par boarId */
    const attachmentsByBoarId = computed(() => {
        const map = new Map<number, IAttachment[]>();
        for (const a of attachmentStore.datas) {
            if (!a.boarId) continue;
            let list = map.get(a.boarId);
            if (!list) { list = []; map.set(a.boarId, list); }
            list.push(a);
        }
        return map;
    });

    /** Analyses de production indexées par lot ID */
    const analysesByLotId = computed(() => {
        const map = new Map<number, IProductionAnalyse[]>();
        for (const a of productionAnalysisStore.datas) {
            const lotId = a.lot?.id;
            if (!lotId) continue;
            let list = map.get(lotId);
            if (!list) { list = []; map.set(lotId, list); }
            list.push(a);
        }
        return map;
    });

    /** IDs des groupes du plan de monte courant */
    const currentPlanGroupIds = computed(() => {
        return new Set(
            groupMountingStore.datas
                .filter(g => g.mountingPlanId === cubicleStore.mountingPlanId)
                .map(g => g.id)
        );
    });

    /** Codes de type de protocole sanitaire par boarId */
    const protocolTypesByBoarId = computed(() => {
        const map = new Map<number, string[]>();
        for (const p of sanitaryProtocolStore.datas) {
            if (!p.sanitaryProtocolAnimals) continue;
            for (const spa of p.sanitaryProtocolAnimals) {
                if (!spa.boarId) continue;
                let list = map.get(spa.boarId);
                if (!list) { list = []; map.set(spa.boarId, list); }
                if (!list.includes(p.typeProtocol.code)) {
                    list.push(p.typeProtocol.code);
                }
            }
        }
        return map;
    });

    /**
     * Premium status pré-calculé par boarId.
     *
     * Séparé de tileDataByBoarId pour éviter que le recalcul complet (O(B×P×A))
     * se déclenche lors de simples déplacements de cubicles.
     * Dépendances : boarsStore, productionsByAnimalId, lotsByProductionId,
     *               analysesByLotId, optionStore — mais PAS cubicleStore.
     *
     * Pré-cache les seuils par combinaison (schéma, race) pour réduire
     * les appels à getOptionByCodeForCenter de O(B×6) à O(combos×6).
     */
    const isPremiumByBoarId = computed(() => {
        const result = new Map<number, boolean>();

        // Lire l'option une seule fois pour tous les verrats
        const requiredControls = +(optionStore.getOptionByCodeForCenter(PREMIUM_OPTIONS.PREMIUM_REQUIRED_CONTROLS_AMOUNT)?.value ?? 4);

        // Cache des seuils par combinaison (schéma, race) — O(combos) lookups au lieu de O(B)
        const thresholdCache = new Map<string, { minUseful: number; minLiving: number; minMotility: number }>();

        for (const boar of boarsStore.datas) {
            // Fast-path : seuls les verrats code3 "1" peuvent être premium
            if (boar.conservationAptitude?.code3 !== "1") {
                result.set(boar.id, false);
                continue;
            }

            const schema = boar.geneticType?.code3 ?? "";
            const breed = Number(boar.breed?.code3);
            const key = `${schema}_${breed}`;
            if (!thresholdCache.has(key)) {
                thresholdCache.set(key, {
                    minUseful:   getPremiumThreshold(PREMIUM_OPTIONS.PREMIUM_CASA_USEFUL_THRESHOLD,      schema, breed, 65),
                    minLiving:   getPremiumThreshold(PREMIUM_OPTIONS.PREMIUM_OPERATOR_LIVING_THRESHOLD,  schema, breed, 70),
                    minMotility: getPremiumThreshold(PREMIUM_OPTIONS.PREMIUM_OPERATOR_MOTILITY_THRESHOLD, schema, breed, 2),
                });
            }

            result.set(boar.id, computeIsPremium(boar, requiredControls, thresholdCache.get(key)));
        }
        return result;
    });

    // ==========================================
    // COMPUTED PRINCIPAL : données pré-calculées par boarId
    // ==========================================

    const tileDataByBoarId = computed(() => {
        const result = new Map<number, TileBoarData>();
        const cubicles = cubicleStore.getForBuilding as ICubicle[];

        // Options par défaut
        const defaultConcOption = optionStore.getOptionByCodeForCenter(CONCENTRATION_FINALE_OPTION);
        const defaultVolOption = optionStore.getOptionByCodeForCenter(VOLUME_STANDARD_OPTION);
        const defaultConc = defaultConcOption?.value ? +defaultConcOption.value : 0;
        const defaultVol = defaultVolOption?.value ? +defaultVolOption.value : 0;

        const groupIds = currentPlanGroupIds.value;

        // Create groupMounting map for O(1) lookups
        const groupMountingMap = new Map(groupMountingStore.datas.map(g => [g.id, g]));

        for (const cubicle of cubicles) {
            if (!cubicle.boarId) continue;
            const boar = (cubicle.boar ?? boarsStore.getByIdInStore(cubicle.boarId)) as IBoar | undefined;
            if (!boar) continue;
            if (result.has(cubicle.boarId)) continue; // déjà calculé pour ce verrat
            const boarId = boar.id;

            const spzAverage = computeAverageSpz(boarId, defaultConc, defaultVol);
            const { group, groupColor } = computeBoarGroup(boarId, groupIds, groupMountingMap);
            const lastProdInterval = computeDaysFromLastProd(boarId);
            const premium = isPremiumByBoarId.value.get(boarId) ?? false;
            const closePlanIds = computeClosePlanIds(boarId);

            const splitName = boar.name.split(' ');

            result.set(boarId, {
                schemaCode: boar.geneticType?.code3 ?? '',
                name: boar.name,
                rawName: splitName[splitName.length - 1],
                workNumber: boar.workNumber ?? '',
                armada: !!boar.armada,
                nnCode: boar.nn?.code3 ?? '',
                studInfo: boar.studInfo ?? '',
                spzAverage,
                group,
                groupColor,
                conservationStatus: boar.productionStatus?.code3 ?? '',
                conservationApt: boar.conservationAptitude?.code3 ?? '',
                lastProdInterval,
                premium,
                closePlanIds
            });
        }

        return result;
    });

    // ==========================================
    // FONCTIONS DE CALCUL PRIVÉES (utilisant les Maps indexées)
    // ==========================================

    /** Réplique MountingPlanBoarComposable.getAverageSpzNumber() */
    function computeAverageSpz(boarId: number, defaultConc: number, defaultVol: number): string {
        if (!defaultConc || !defaultVol) return "0";

        const productions = productionsByAnimalId.value.get(boarId) ?? [];
        const compliant = productions
            .filter(p => p.productionState.toString() === StateOfProduction[StateOfProduction.Compliant])
            .sort((a, b) => new Date(b.addingDate).getTime() - new Date(a.addingDate).getTime())
            .slice(0, 3);

        if (compliant.length === 0) return "0";

        let totalPotential = 0;
        for (const prod of compliant) {
            const lots = lotsByProductionId.value.get(prod.id) ?? [];
            for (const lot of lots) {
                const vol = volumeLot(lot);
                const conc = concentrationLot(lot);
                totalPotential += ((vol * conc) / defaultConc) / defaultVol;
            }
        }

        return `${Math.ceil(totalPotential / compliant.length)}`;
    }

    /** Réplique MountingPlanTileComposable.getBoarTileGroup() et getBoarTileGroupColor() */
    function computeBoarGroup(boarId: number, groupIds: Set<number>, groupMountingMap: Map<number, any>): { group: string, groupColor: string } {
        const attachments = attachmentsByBoarId.value.get(boarId) ?? [];
        // Use groupMountingId instead of groupMounting.id to avoid undefined access
        const match = attachments.find(a => a.groupMountingId && groupIds.has(a.groupMountingId));

        if (!match) return { group: '', groupColor: '' };

        // Get groupMounting from Map for O(1) lookup instead of find() O(n)
        const groupMounting = groupMountingMap.get(match.groupMountingId);
        if (!groupMounting) return { group: '', groupColor: '' };

        return {
            group: 'G' + (groupMounting.number ?? ''),
            groupColor: groupMounting.color ?? ''
        };
    }

    /** Réplique MountingPlanTileComposable.getDaysFromLastProduction() */
    function computeDaysFromLastProd(boarId: number): string {
        const productions = productionsByAnimalId.value.get(boarId) ?? [];
        if (productions.length === 0) return "N/A";

        let latestTime = 0;
        let latestProd: IProduction | null = null;
        for (const p of productions) {
            const t = new Date(p.addingDate).getTime();
            if (t > latestTime) { latestTime = t; latestProd = p; }
        }

        if (!latestProd) return "N/A";
        return `${numberJourBetween(new Date(), latestProd.addingDate)}`;
    }

    /** Réplique MountingPlanTileComposable.isPremiumBoar()
     *  @param cachedRequiredControls  pré-calculé par isPremiumByBoarId (évite un getOption par verrat)
     *  @param cachedThresholds        pré-calculé par isPremiumByBoarId (évite 6 getOption par verrat)
     */
    function computeIsPremium(
        boar: IBoar,
        cachedRequiredControls?: number,
        cachedThresholds?: { minUseful: number; minLiving: number; minMotility: number }
    ): boolean {
        if (boar.conservationAptitude?.code3 !== "1") return false;

        const productions = productionsByAnimalId.value.get(boar.id) ?? [];
        const sorted = [...productions].sort(
            (a, b) => new Date(a.dateProduction).getTime() - new Date(b.dateProduction).getTime()
        );

        // Première production de chaque jour
        const boarProdIds: number[] = [];
        const seenDays = new Set<string>();
        for (const prod of sorted) {
            const day = new Date(prod.dateProduction).toISOString().slice(0, 10);
            if (!seenDays.has(day)) {
                boarProdIds.push(prod.id);
                seenDays.add(day);
            }
        }

        // Lots de ces productions (via index) : map lot.id → dateProduction + map prodId → lots
        const lotProdDate = new Map<number, Date>();
        const lotsByProd = new Map<number, ILot[]>();
        for (const prodId of boarProdIds) {
            const lots = lotsByProductionId.value.get(prodId) ?? [];
            lotsByProd.set(prodId, lots);
            for (const lot of lots) {
                if (lot.production?.dateProduction) {
                    lotProdDate.set(lot.id, lot.production.dateProduction);
                }
            }
        }

        // Pour chaque production (dans l'ordre), déterminer le meilleur contrôle : J+4 prioritaire sur J+3
        const bestControlByProdId = new Map<number, IProductionAnalyse>();
        for (const prodId of boarProdIds) {
            const lots = lotsByProd.get(prodId) ?? [];
            let bestJ4: IProductionAnalyse | undefined;
            let bestJ3: IProductionAnalyse | undefined;
            for (const lot of lots) {
                const lotAnalyses = analysesByLotId.value.get(lot.id) ?? [];
                for (const a of lotAnalyses) {
                    if (a.isControl && !a.isExternal && lotProdDate.has(a.lot.id)) {
                        const prodDate = new Date(lotProdDate.get(a.lot.id)!);
                        const prodDateDay = new Date(prodDate.getFullYear(), prodDate.getMonth(), prodDate.getDate());
                        const analyseDateDay = new Date(new Date(a.dateAnalyse).getFullYear(), new Date(a.dateAnalyse).getMonth(), new Date(a.dateAnalyse).getDate());
                        const dayDiff = Math.abs(getDateDiff(prodDateDay, analyseDateDay).day);
                        if (dayDiff === 4) bestJ4 = a;
                        else if (dayDiff === 3) bestJ3 = a;
                    }
                }
            }
            const best = bestJ4 ?? bestJ3;
            if (best) bestControlByProdId.set(prodId, best);
        }

        // Contrôles depuis la dernière production — les productions sans contrôle J+3/J+4 sont ignorées
        const consecutiveControls: IProductionAnalyse[] = [];
        for (let i = boarProdIds.length - 1; i >= 0; i--) {
            const control = bestControlByProdId.get(boarProdIds[i]);
            if (!control) continue;
            consecutiveControls.unshift(control);
        }

        // Nombre de contrôles consécutifs requis (utilise le cache si disponible)
        const requiredControlsAmount = cachedRequiredControls
            ?? +(optionStore.getOptionByCodeForCenter(PREMIUM_OPTIONS.PREMIUM_REQUIRED_CONTROLS_AMOUNT)?.value ?? 4);

        if (consecutiveControls.length < requiredControlsAmount) return false;

        const latestControls = consecutiveControls.slice(-requiredControlsAmount);

        // Seuils (utilise le cache si disponible, sinon calcul à la volée)
        const schema = boar.geneticType?.code3 ?? "";
        const breed = Number(boar.breed?.code3);
        const { minUseful, minLiving, minMotility } = cachedThresholds ?? {
            minUseful:   getPremiumThreshold(PREMIUM_OPTIONS.PREMIUM_CASA_USEFUL_THRESHOLD,      schema, breed, 65),
            minLiving:   getPremiumThreshold(PREMIUM_OPTIONS.PREMIUM_OPERATOR_LIVING_THRESHOLD,  schema, breed, 70),
            minMotility: getPremiumThreshold(PREMIUM_OPTIONS.PREMIUM_OPERATOR_MOTILITY_THRESHOLD, schema, breed, 2),
        };

        for (const control of latestControls) {
            if (control.isUsedCasa) {
                if (control.prcUtile < minUseful) return false;
            } else {
                if (control.prcLiving < minLiving || control.motility < minMotility) return false;
            }
        }

        return true;
    }

    /** Réplique MountingPlanTileComposable.getClosePlansIds() */
    function computeClosePlanIds(boarId: number): number[] {
        const currentPlanId = cubicleStore.mountingPlanId;
        const buildingPlans = mountingPlanStore.getMountingPlanDateAscending;
        const buildingPlanIds = new Set(buildingPlans.filter(p => p.id !== currentPlanId).map(p => p.id));
        if (buildingPlanIds.size === 0) return [];

        const attachments = attachmentsByBoarId.value.get(boarId) ?? [];
        // Use groupMountingId instead of groupMounting.id to avoid undefined access
        const boarGroupIds = new Set(
            attachments.filter(a => a.groupMountingId).map(a => a.groupMountingId)
        );
        if (boarGroupIds.size === 0) return [];

        const validPlanIds: number[] = [];
        for (const gm of groupMountingStore.datas) {
            // Use mountingPlanId instead of mountingPlan.id to avoid undefined access
            if (!gm.id || !gm.mountingPlanId) continue;
            if (boarGroupIds.has(gm.id) && buildingPlanIds.has(gm.mountingPlanId)) {
                validPlanIds.push(gm.mountingPlanId);
            }
        }
        return validPlanIds;
    }

    // === Helpers pour les seuils premium ===
    function getPremiumThreshold(baseOption: string, schema: string, breed: number, defaultVal: number): number {
        const schemaOption = optionStore.getOptionByCodeForCenter(baseOption + `_${schema}`);
        if (schemaOption?.value) return +schemaOption.value;
        const breedOption = optionStore.getOptionByCodeForCenter(baseOption + `_${breed}`);
        if (breedOption?.value) return +breedOption.value;
        return defaultVal;
    }

    // === Fonctions volume/concentration lot (répliquent LotComposable) ===
    function volumeLot(lot: ILot): number {
        if (lot.isHeterospermie) {
            const list = dispatchManufactoringStore.getPartOfLot(lot.id);
            return list.reduce((acc: number, d: any) => acc + d.volume, 0);
        } else {
            if (lot.production?.sample) {
                return lot.production.sample.secondaryVolume > 0
                    ? lot.production.sample.secondaryVolume
                    : lot.production.sample.volume;
            }
            return 0;
        }
    }

    function concentrationLot(lot: ILot): number {
        if (lot.isHeterospermie) return lot.concentration;
        if (lot.production?.sample) {
            return lot.production.sample.secondaryConcentration > 0
                ? lot.production.sample.secondaryConcentration
                : lot.production.sample.concentration;
        }
        return 0;
    }

    /**
     * Secteurs actifs par cubicleId — calculé UNE FOIS pour tous les cubicles.
     *
     * Remplace le computed `cubicleSectors` local à chaque tuile de l'ancien composant par logette,
     * qui effectuait un filter O(n×s) par tuile à chaque changement de selectedSectorIds.
     * Dépendances : cubicleStore.selectedSectorIds, sectorCubicleStore.sectorsByCubicleId.
     * Ne dépend PAS de tileDataByBoarId ni des productions.
     */
    const cubicleSectorsMap = computed((): Map<number, ISector[]> => {
        const result = new Map<number, ISector[]>();
        const selectedSet = new Set(cubicleStore.selectedSectorIds);

        if (selectedSet.size === 0) return result;

        for (const [cubicleId, sectorCubicles] of sectorCubicleStore.sectorsByCubicleId) {
            const sectors = sectorCubicles
                .filter(sc => selectedSet.has(sc.sectorId))
                .map(sc => sc.sector)
                .filter((s): s is ISector => s !== undefined)
                .sort((a, b) => a.id - b.id);

            if (sectors.length > 0) result.set(cubicleId, sectors);
        }
        return result;
    });

    /**
     * SPZ moyen par verrat, calculé indépendamment du filtre bâtiment.
     * Utilisé par getSchemaList (cubicle.store) pour nbrTotalDoseCommercial,
     * dont le calcul doit être disponible même avant que buildingId soit positionné.
     */
    const spzAverageByBoarId = computed((): Map<number, number> => {
        const result = new Map<number, number>();
        const defaultConcOption = optionStore.getOptionByCodeForCenter(CONCENTRATION_FINALE_OPTION);
        const defaultVolOption  = optionStore.getOptionByCodeForCenter(VOLUME_STANDARD_OPTION);
        const defaultConc = defaultConcOption?.value ? +defaultConcOption.value : 0;
        const defaultVol  = defaultVolOption?.value  ? +defaultVolOption.value  : 0;
        for (const boarId of productionsByAnimalId.value.keys()) {
            result.set(boarId, Number(computeAverageSpz(boarId, defaultConc, defaultVol)));
        }
        return result;
    });

    return {
        tileDataByBoarId,
        isPremiumByBoarId,
        cubicleSectorsMap,
        productionsByAnimalId,
        attachmentsByBoarId,
        currentPlanGroupIds,
        protocolTypesByBoarId,
        spzAverageByBoarId,
    };
});
